<script setup>
/*
    Board.vue -- Display board area with widgets
 */
import {onUnmounted, onMounted, reactive, ref, watch} from 'vue'
import {Progress, State} from '@/paks/vu-app'
import {clone, delay, fixShadow, getValue, waitRender} from '@/paks/vu-app'
import Widgets from './Widgets.vue'
import WidgetEdit from './WidgetEdit.vue'
import ToolbarWidget from '@/paks/vu-widgets/ToolbarWidget.vue'
import Toolbox from './Toolbox.vue'
import DashEdit from './DashEdit.vue'
import PageEdit from './PageEdit.vue'

const props = defineProps({
    design: Boolean,
    modelValue: Object,
    options: {type: Object, default: {}},
    view: Object, // View from display for pages
})

const page = reactive({
    canDash: true,
    canEdit: null,
    currentRange: null,
    board: {widgets: []},
    mobile: State.app.mobile,
    priorView: null,
    range: {anchor: 'relative', period: 3600},
    ready: false,
    refresh: 60,
    resizing: null,
    saving: null,
    showDashEdit: false,
    showWidgetEdit: false,
    view: {},
    widget: {},
})

//  Component references
const container = ref(null)
const widgets = ref(null)
const confirm = ref(null)
const widgetEdit = ref(null)
const toolbar = ref(null)

defineExpose({page})

/*
    editBoard - Invoked by toolbar widget to open the board edit panel
    expand - Invoked by the toolbar widget to expand the board
    refreshData - Invoked by various refresh icons to refresh the data
    update - Invoked by when dark mode changes to update the board with a full render
 */
watch(
    () => State.app.needs.editBoard,
    (v) => v && editBoard(page.board)
)
watch(
    () => State.app.needs.editWidget,
    (v) => v && editWidget(v)
)
watch(
    () => State.app.needs.expandWidgets,
    async (v) => v && (await render({initialize: true, expand: true, fetch: true}))
)
watch(
    () => State.app.needs.refreshData,
    async (v) => v && (await render({layout: false, fetch: true}))
)
watch(
    () => State.app.needs.updateBoard,
    async (v) => v && (await render())
)
watch(
    () => props.modelValue,
    async () => {
        page.board = clone(props.modelValue)
        if (props.view) {
            page.view = clone(props.view)
            page.view.board = page.board
        }
        await render({initialize: true, layout: true, wait: true})
    },
    {deep: true}
)

onMounted(async () => {
    page.board = clone(props.modelValue)
    page.view = clone(props.view)
    page.priorView = clone(page.view)
    if (page.view) {
        page.view.board = page.board
    }
    window.addEventListener('resize', resizeWindow)
    page.board._remaining = 0
    State.app.design = true
    page.ready = true
    daemon()
})

onUnmounted(() => {
    State.app.design = false
    page.ready = false
    window.removeEventListener('resize', resizeWindow)
    fixShadow()
})

async function render(params = {}) {
    if (params.progress !== false) {
        Progress.start('repeat')
    }
    State.app.noNeed('board')
    if (params.initialize) {
        params.wait = true
        await waitRender()
    }
    if (!widgets.value && params.wait !== false) {
        await waitRender()
    }
    page.canEdit = props.design

    /*
        Give preference to board setting, route setting, config (from product.json5 & display.timeouts)
     */
    let board = page.board
    page.refresh = getValue(
        board.refresh || page.view?.refresh || State.config?.timeouts?.dashboard || 60
    )
    board._remaining = page.refresh
    Object.assign(page.range, board.range)
    page.currentRange = clone(page.range)

    if (container.value && board.css) {
        for (let {property, value} of board.css) {
            container.value.style[property] = value
        }
    }
    if (widgets.value) {
        await widgets.value.update(params)
    }
    if (toolbar.value) {
        await toolbar.value.update(params)
    }
    if (params.progress !== false) {
        Progress.stop()
    }
}

async function resizeWindow() {
    if (!page.resizing) {
        page.resizing = setTimeout(async () => {
            if (widgets.value) {
                await render({initialize: true, fetch: true, progress: false, wait: true})
            }
            page.resizing = null
        }, 500)
    }
}

async function daemon() {
    //  First time, initialize widgets, then fetch only. Expand if using a new widget set.
    let params = {initialize: true, expand: props.options?.expand}

    while (page.ready) {
        while (page.board._remaining > 0) {
            await delay(1000)
            if (page.board._remaining > 0 && !props.design) {
                page.board._remaining--
            }
        }
        if (State.auth.ready) {
            await render(params)
        }
        page.board._remaining = page.refresh
        params = {fetch: true, layout: false}
    }
}

async function editBoard(board) {
    page.showDashEdit = board ? true : false
    if (board) {
        board.range = page.range
        page.board = Object.assign({}, board)
        if (page.view) {
            page.view.board = page.board
        }
    }
}

/*
    Control the widget edit panel. Widget is set for open, null for close.
 */
async function editWidget(widget) {
    page.showWidgetEdit = widget ? true : false
    if (widget) {
        if (!widget.id) {
            let cloudId = State.app.prefer('cloudId')
            widget = {type: 'graph', css: [], range: {}, cloudId, anchor: {}}
            if (!widget.cloudId && State.config.builder) {
                widget.cloudId = 'builder'
            }
        }
        //  Open panel
        page.widget = widget
    } else {
        if (page.widget._new) {
            await removeWidget(page.widget)
        }
    }
    window.scrollTo({top: 0, behavior: 'smooth'})
}

async function applyWidget(widget) {
    let board = page.board
    if (widget) {
        let index = board.widgets.findIndex((v) => v.id == widget.id)
        if (index >= 0) {
            board.widgets[index] = widget
        } else {
            createWidget(board, widget)
        }
    }
    await render({initialize: true})
}

async function removeWidget(widget) {
    let index = page.board.widgets.findIndex((w) => w.id == widget.id)
    if (index >= 0) {
        page.board.widgets.splice(index, 1)
    }
    await render({initialize: true})
}

//  Called for widget resize, move
async function updateWidgets(list) {
    let board = page.board
    for (let widget of list) {
        let index = board.widgets.findIndex((v) => v.id == widget.id)
        if (index >= 0) {
            board.widgets.splice(index, 1, widget)
        } else {
            createWidget(board, widget)
        }
    }
    await render({initialize: true, fetch: true})
    State.app.setNeedSave(page.board)
}

//  Private Functions

function createWidget(board, widget, show = true) {
    let dv = board.widgets.find((v) => v.id == widget.id)
    if (!dv) {
        board.widgets.push(widget)
    }
}

async function confirmCloseWidgetEdit(discard) {
    let widget = widgetEdit.value?.page?.widget
    if (discard && widget && !widget.id && (widget.css.length || widget.model || widget.metric)) {
        if (await confirm.value.ask(`Do you want to discard changes? `)) {
            return true
        }
        return false
    }
    return true
}
</script>

<template>
    <div
        class="board"
        :class="props.design ? 'design' : '' + page.board.toolbar ? ' with-toolbar' : ''">
        <Toolbox v-if="props.design" :dashboard="page.board" :container="container" />
        <div class="board-region">
            <ToolbarWidget
                v-if="page.ready && (props.design || page.board.toolbar)"
                ref="toolbar"
                class="board-toolbar"
                :class="`${props.design ? 'design' : ''}`"
                :board="page.board"
                :design="props.design"
                :state="State"
                :view="page.view" />

            <div
                class="board-container"
                :class="`${props.design ? 'design' : 'fixed'} 
                    ${page.mobile ? 'mobile' : ''} 
                    ${page.board.emulate == 'Mobile' ? 'emulate-mobile' : ''} 
                    ${page.board.type == 'dashboard' ? 'dashboard' : ''} 
                    ${page.board.layout == 'grid' && props.design ? 'grid' : ''} 
                    ${State.app.dark ? 'dark' : 'light'} `"
                ref="container">
                <Widgets
                    v-if="page.ready"
                    class="board-widgets"
                    :dashboard="page.board"
                    :design="props.design"
                    :layout="page.board.layout"
                    :range="page.range"
                    @remove="removeWidget"
                    @edit="editWidget"
                    @update="updateWidgets"
                    ref="widgets" />
            </div>

            <vu-panel
                v-model="page.showWidgetEdit"
                :confirm="confirmCloseWidgetEdit"
                @close="editWidget(null)"
                :widths="['700px']"
                :keyboard="false">
                <WidgetEdit
                    ref="widgetEdit"
                    v-model="page.widget"
                    :board="page.board"
                    @apply="applyWidget"
                    @close="editWidget(null)"
                    @remove="removeWidget" />
            </vu-panel>

            <vu-panel
                v-model="page.showDashEdit"
                @close="editBoard(null)"
                :widths="['700px']"
                :keyboard="false">
                <DashEdit
                    v-if="page.board.type == 'dashboard'"
                    v-model="page.board"
                    @close="editBoard" />
                <PageEdit v-else v-model="page.view" @close="editBoard" />
            </vu-panel>

            <div v-if="!page.ready">
                <div class="loading">loading ...</div>
            </div>
            <vu-confirm ref="confirm" />
        </div>
    </div>
</template>

<style lang="scss">
.board {
    display: flex;
    flex-direction: row;
    height: max(calc(100vh - 104px), calc(100% - 34px));
    .toolbox {
        height: inherit;
    }
    .board-region {
        width: 100%;
        // Can't do this as it prevents the backgrouund covering all widgets
        // height: calc(100vh - 104px);
        .board-toolbar {
            background: rgb(var(--v-theme-surface)) !important;
            border-bottom: 1px solid rgb(var(--v-theme-border));
            padding: 10px 32px 6px 20px;
            width: 100%;
            @media (max-width: 640px) {
                padding: 8px 32px 2px 10px;
            }
            .v-toolbar__content {
                overflow: visible !important;
            }
        }
        .board-container {
            background: rgb(var(--v-theme-background));
            width: 100%;
            height: max(calc(100vh - 172px), calc(100% - 172px));
            max-width: none;
            padding: 20px;
            @media (max-width: 640px) {
                padding: 10px;
            }
        }
        .board-container.grid.light {
            background-image: linear-gradient(90deg, rgba(0, 0, 0, 0.1) 1px, transparent 1px),
                linear-gradient(180deg, rgba(0, 0, 0, 0.1) 1px, transparent 1px);
            background-size: 20px 20px;
            background-repeat: repeat;
        }
        .board-container.grid.dark {
            background-image: linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                linear-gradient(180deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
            background-size: 20px 20px;
            background-repeat: repeat;
        }
        .board-container.emulate-mobile.design {
            width: 430px;
            height: calc(932px - 48px);
            position: absolute;
        }
        .board-container.emulate-mobile:not(.design) {
            width: 430px;
            height: calc(932px - 48px);
            position: absolute;
            left: 0px;
        }
        .board-widgets {
            padding: 0;
            margin: 0px;
        }
        .dispatcher {
            width: 100%;
        }
        .v-progress-circular__content {
            font-size: 12px;
        }
    }
}
.board.with-toolbar {
    height: calc(100% - 80px);
}
</style>
